import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Form, Input, Typography, DatePicker, Button, InputNumber, Radio, Tooltip, message } from "antd";
import { InfoCircleTwoTone } from "@ant-design/icons";
import moment, { Moment } from "moment";
import locale from "antd/es/date-picker/locale/zh_TW";
import { useSelector } from "react-redux";
import { RootState } from "@redux/rootReducer";
import { useAppDispatch } from "src/store";
import {
  fetchLogisticSettings,
  postLogisticSettings,
  postLogisticSettingsPreview,
  clearPreviewEstimatedArrival,
} from "@redux/configSettingSlice";
import "moment/locale/zh-tw";

const { Title } = Typography;

const TitleStyled = styled(Title)`
  line-height: 36px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const RowTitle = styled.div`
  align-self: flex-start;
  text-align: left;
  line-height: 32px;
  margin-right: 40px;
  font-size: 14px;
  white-space: nowrap;
`;

const RowContainer = styled.div`
  display: flex;
  gap: 50px;
  margin-bottom: 50px;
`;

const RowContent = styled.div<{ width: string | number }>`
  width: ${({ width }) => (typeof width === "number" ? `${width}px` : width)};
  height: 100%;
`;

const PreviewSection = styled.div`
  margin-block: 24px;
  height: 35vh;
  display: flex;
  gap: 10%;
  border: 1px solid ${({ theme }) => theme.colorNeutral300};
  padding: 20px;
  border-radius: 10px;
`;

const SettingContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EstimatedArrivalContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const RequiredIcon = styled.span`
  color: red;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background-color: rgb(242, 242, 242);
  padding-block: 20px;
`;

const CancelButton = styled(Button)`
  margin-right: 15px;
  color: ${({ theme }) => theme.colorNeutral500};
  border-color: ${({ theme }) => theme.colorNeutral500};
  background-color: ${({ theme }) => theme.colorNeutral100};
  width: 100px;
`;

const InfoIcon = styled(InfoCircleTwoTone)`
  margin-left: 4px;
  cursor: pointer;
`;

const LogisticsSection = styled.div`
  margin-bottom: 50px;
`;

const LogisticsRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  align-items: flex-start;

  .ant-form-item-required::before,
  .ant-form-item-required::after {
    display: none !important;
  }
`;

const EstimatedArrivalDisplay = styled.div`
  padding: 25px 30px;
  background-color: #eff6ff;
  width: 100%;
  height: 100%;
  border-radius: 10px;
  position: relative;
`;

const RadioGroup = styled(Radio.Group)`
  .ant-radio-wrapper {
    margin-right: 24px;
  }
`;

const CustomRangePicker = styled(DatePicker.RangePicker)`
  width: 100%;
`;

const SettingsSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const Subsection = styled.div`
  display: flex;
  gap: 50px;
`;

const EstimatedArrivalText = styled.span`
  position: absolute;
  bottom: 50%;
  left: 30px;
  transform: translateY(50%);
  font-size: 30px;
  font-weight: 500;
`;

const INPUT_WIDTH = 200;

const buildPayloadFromFormValues = (values: Record<string, any>) => ({
  deployCvsSuspendDisplayName: values.deployCvsSuspendDisplayName,
  deployCvsSuspendStartDate: values.deployCvsSuspendDateRange?.[0]?.format("YYYY-MM-DD"),
  deployCvsSuspendEndDate: values.deployCvsSuspendDateRange?.[1]?.format("YYYY-MM-DD"),
  tcatProcessingDays: values.tcatProcessingDays,
  tcatDeliveringDays: values.tcatDeliveringDays,
  storeProcessingDays: values.storeProcessingDays,
  storeDeliveringDays: values.storeDeliveringDays,
});

const HolidayShippingSettings = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { logisticSettingsResult, previewEstimatedArrival, isLogisticSettingsSubmitting } = useSelector(
    (state: RootState) => state.configSetting,
  );
  const [previewOrderDate, setPreviewOrderDate] = useState<Moment | null>(null);
  const [previewDeliveryMethod, setPreviewDeliveryMethod] = useState<"home" | "convenience">("home");

  useEffect(() => {
    dispatch(fetchLogisticSettings());
  }, [dispatch]);

  const resetForm = useCallback(() => {
    if (logisticSettingsResult) {
      const { deployCvsSuspendStartDate, deployCvsSuspendEndDate, ...rest } = logisticSettingsResult;
      const deployCvsSuspendDateRange =
        deployCvsSuspendStartDate && deployCvsSuspendEndDate
          ? [moment(deployCvsSuspendStartDate), moment(deployCvsSuspendEndDate)]
          : null;

      form.setFieldsValue({
        ...rest,
        deployCvsSuspendDateRange,
      });
    }
  }, [form, logisticSettingsResult]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload = buildPayloadFromFormValues(values);
    dispatch(postLogisticSettings(payload));
  };

  const handlePreview = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }

    if (!previewOrderDate) {
      message.warning("請輸入模擬客人下單日期");
      return;
    }

    const values = form.getFieldsValue();
    const body = buildPayloadFromFormValues(values);
    dispatch(
      postLogisticSettingsPreview({
        body,
        isHome: previewDeliveryMethod === "home",
        estOrderDate: previewOrderDate.format("YYYY-MM-DD"),
      }),
    );
  };

  const handleClear = () => {
    resetForm();
    setPreviewOrderDate(null);
    setPreviewDeliveryMethod("home");
    dispatch(clearPreviewEstimatedArrival());
  };

  return (
    <main>
      <Form form={form} layout="vertical">
        {/* 連假與公休日期區間 */}
        <TitleStyled level={4} style={{ marginTop: "20px" }}>
          連假與公休日期區間
        </TitleStyled>
        <RowContainer>
          <Form.Item name="deployCvsSuspendDisplayName" label="連假名稱">
            <Input placeholder="請填寫" allowClear style={{ width: INPUT_WIDTH }} />
          </Form.Item>
          <Form.Item name="deployCvsSuspendDateRange" label="時間區間">
            <CustomRangePicker locale={locale} />
          </Form.Item>
        </RowContainer>
        {/* 物流時效基礎設定 */}
        <TitleStyled level={4}>
          物流時效基礎設定（週六、週日一律順延）
          <RequiredIcon>*</RequiredIcon>
          <Tooltip
            placement="top"
            title="本設定以「平日」為計算基礎。週六、週日不列入物流作業日,將自動順延。若設定「連假/公休不拋單區間」,該期間內將不進行拋單與到貨日計算。"
          >
            <InfoIcon />
          </Tooltip>
        </TitleStyled>
        <LogisticsSection>
          {/* 黑貓宅配 */}
          <LogisticsRow>
            <TitleStyled level={5}>黑貓宅配</TitleStyled>
            <Subsection>
              <Form.Item
                name="tcatProcessingDays"
                label="倉庫作業天數"
                rules={[{ required: true, message: "此為必填欄位" }]}
              >
                <InputNumber min={0} style={{ width: INPUT_WIDTH }} placeholder="請輸入" />
              </Form.Item>
              <Form.Item
                name="tcatDeliveringDays"
                label="最長運輸天數"
                rules={[{ required: true, message: "此為必填欄位" }]}
              >
                <InputNumber min={0} style={{ width: INPUT_WIDTH }} placeholder="請輸入" />
              </Form.Item>
            </Subsection>
          </LogisticsRow>
          {/* 超商取貨 */}
          <LogisticsRow>
            <TitleStyled level={5}>超商取貨</TitleStyled>
            <Subsection>
              <Form.Item
                name="storeProcessingDays"
                label="倉庫作業天數"
                rules={[{ required: true, message: "此為必填欄位" }]}
              >
                <InputNumber min={0} style={{ width: INPUT_WIDTH }} placeholder="請輸入" />
              </Form.Item>
              <Form.Item
                name="storeDeliveringDays"
                label="最長運輸天數"
                rules={[{ required: true, message: "此為必填欄位" }]}
              >
                <InputNumber min={0} style={{ width: INPUT_WIDTH }} placeholder="請輸入" />
              </Form.Item>
            </Subsection>
          </LogisticsRow>
        </LogisticsSection>

        {/* 預覽前台顯示 */}
        <PreviewSection>
          <SettingContainer>
            <TitleStyled level={5}>預覽前台顯示</TitleStyled>
            <SettingsSection>
              <Row>
                <RowTitle>模擬客人下單日期</RowTitle>
                <RowContent width={300}>
                  <DatePicker
                    locale={locale}
                    placeholder="請選擇"
                    style={{ width: "100%" }}
                    value={previewOrderDate}
                    onChange={(date) => setPreviewOrderDate(date)}
                  />
                </RowContent>
              </Row>
              <Row>
                <RowTitle>配送方式</RowTitle>
                <RowContent width={300}>
                  <RadioGroup value={previewDeliveryMethod} onChange={(e) => setPreviewDeliveryMethod(e.target.value)}>
                    <Radio value="home">宅配</Radio>
                    <Radio value="convenience">超商配送</Radio>
                  </RadioGroup>
                </RowContent>
              </Row>
            </SettingsSection>
          </SettingContainer>
          {/* 預計到貨時間 */}
          <EstimatedArrivalContainer>
            <RowContent width="100%">
              <EstimatedArrivalDisplay>
                <TitleStyled level={5}>預計到貨時間</TitleStyled>
                <EstimatedArrivalText>
                  {previewEstimatedArrival ? (
                    <>
                      {moment(previewEstimatedArrival).format("YYYY/ MM/ DD")}（
                      {moment(previewEstimatedArrival).locale("zh-tw").format("ddd")}）
                    </>
                  ) : (
                    "(請填畢所有設定以推估預計到貨時間)"
                  )}
                </EstimatedArrivalText>
              </EstimatedArrivalDisplay>
            </RowContent>
          </EstimatedArrivalContainer>
        </PreviewSection>

        {/* 操作按鈕 */}
        <ButtonContainer>
          <CancelButton onClick={handleClear}>重置</CancelButton>
          <Button onClick={handlePreview} style={{ color: "#1890ff", borderColor: "#1890ff" }}>
            預覽
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={isLogisticSettingsSubmitting}
            style={{ marginRight: "20px" }}
          >
            送出
          </Button>
        </ButtonContainer>
      </Form>
    </main>
  );
};

export default HolidayShippingSettings;
